import { useState } from 'react';
import { Star, Send, Gift, X, Sparkles } from 'lucide-react';
import { apiFetch, toast } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ReviewReferralCard({ onClose, ticketId = null, resolutionSummary = '' }) {
  const [activeTab, setActiveTab] = useState('review');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [allowPublic, setAllowPublic] = useState(true);
  const [referralEmail, setReferralEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAiHint, setShowAiHint] = useState(false);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewText.trim().length < 15) {
      toast.error('Please write at least a couple of sentences about your experience.');
      return;
    }
    setLoading(true);

    try {
      const response = await apiFetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          review_text: reviewText,
          ticket_id: ticketId,
          allow_public: allowPublic,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        if (allowPublic && rating === 5) {
          toast.success('Thank you! Your testimonial is now live on our site.');
        } else if (allowPublic && rating === 4) {
          toast.success('Thanks! Your review will be published after a quick review.');
        }
        setTimeout(() => {
          if (onClose) onClose();
        }, 2500);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiFetch(`${API}/referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referral_email: referralEmail,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setReferralEmail('');
          setSubmitted(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting referral:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass rounded-xl p-8 glow-box text-center" data-testid="review-success-message">
        <div className="text-6xl mb-4">✨</div>
        <h3 className="text-2xl font-bold text-[#00ff00] glow mb-2">Thank You!</h3>
        <p className="text-[#00f0ff]">
          {activeTab === 'review'
            ? 'Your review helps us improve our service!'
            : 'We appreciate you spreading the word!'}
        </p>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-6 glow-box-magenta" data-testid="review-referral-card">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-[#ff00ff] to-[#00f0ff] text-transparent bg-clip-text">
          Love Our Service?
        </h3>
        {onClose && (
          <button onClick={onClose} className="text-[#00f0ff] hover:text-[#ff00ff]">
            <X size={24} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setActiveTab('review')}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
            activeTab === 'review'
              ? 'bg-[#ff00ff] text-white glow-box-magenta'
              : 'border border-[#ff00ff] text-[#ff00ff] hover:bg-[#ff00ff]/20'
          }`}
          data-testid="review-tab"
        >
          <Star className="inline mr-2" size={18} />
          Leave a Review
        </button>
        <button
          onClick={() => setActiveTab('referral')}
          className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
            activeTab === 'referral'
              ? 'bg-[#00f0ff] text-[#0a0a1f] glow-box'
              : 'border border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff]/20'
          }`}
          data-testid="referral-tab"
        >
          <Gift className="inline mr-2" size={18} />
          Refer a Friend
        </button>
      </div>

      {/* Review Form */}
      {activeTab === 'review' && (
        <form onSubmit={handleReviewSubmit} className="space-y-4">
          <div>
            <label className="block text-[#00f0ff] mb-3 font-semibold text-center">
              How would you rate our service?
            </label>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    size={40}
                    className={`${
                      star <= (hoverRating || rating)
                        ? 'fill-[#ffa500] text-[#ffa500] glow'
                        : 'text-[#00f0ff]/30'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[#00f0ff] mb-2 font-semibold">Your Review</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none min-h-[120px]"
              placeholder="In your own words, how did we do? What problem did we fix?"
              required
              minLength={15}
              data-testid="review-text-input"
            />
            <p className="text-xs text-[#00f0ff]/40 mt-1">
              Min 15 characters · This is what visitors will read on our site if you allow it.
            </p>

            {resolutionSummary && (
              <div className="mt-3">
                {!showAiHint ? (
                  <button
                    type="button"
                    onClick={() => setShowAiHint(true)}
                    className="text-xs text-[#ff00ff]/80 hover:text-[#ff00ff] flex items-center space-x-1 transition-colors"
                    data-testid="show-ai-hint-button"
                  >
                    <Sparkles size={14} />
                    <span>Need a refresher on what we fixed? Tap for a hint.</span>
                  </button>
                ) : (
                  <div className="p-3 rounded-lg bg-[#0a0a1f]/60 border border-[#ff00ff]/20 text-xs text-[#00f0ff]/70" data-testid="ai-hint-box">
                    <p className="mb-2 text-[#ff00ff] font-semibold flex items-center space-x-1">
                      <Sparkles size={12} />
                      <span>Quick recap (just for your reference — please write the review in your own words):</span>
                    </p>
                    <p className="italic whitespace-pre-wrap leading-relaxed">{resolutionSummary}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Allow public toggle */}
          <label
            className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg border border-[#00f0ff]/20 hover:border-[#00f0ff]/50 transition-all"
            data-testid="allow-public-toggle"
          >
            <input
              type="checkbox"
              checked={allowPublic}
              onChange={(e) => setAllowPublic(e.target.checked)}
              className="mt-1 accent-[#ff00ff] w-4 h-4"
              data-testid="allow-public-checkbox"
            />
            <span className="text-sm text-[#00f0ff]/90">
              <span className="font-semibold text-[#00f0ff]">Share as a testimonial</span> on
              <span className="text-[#ff00ff]"> nextwavetechhelp.us</span>. We'll show only your first name.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || rating === 0}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#ff00ff] to-[#b026ff] text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center space-x-2"
            data-testid="submit-review-button"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send size={20} />
                <span>Submit Review</span>
              </>
            )}
          </button>
        </form>
      )}

      {/* Referral Form */}
      {activeTab === 'referral' && (
        <form onSubmit={handleReferralSubmit} className="space-y-4">
          <div className="p-4 bg-[#00f0ff]/10 border border-[#00f0ff] rounded-lg mb-4">
            <p className="text-[#00f0ff] text-sm text-center">
              Know someone who needs tech help? Refer them and we'll send them a special welcome offer! 🎁
            </p>
          </div>

          <div>
            <label className="block text-[#00f0ff] mb-2 font-semibold">Friend's Email</label>
            <input
              type="email"
              value={referralEmail}
              onChange={(e) => setReferralEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
              placeholder="friend@email.com"
              required
              data-testid="referral-email-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#00f0ff] to-[#0080ff] text-white font-bold rounded-lg disabled:opacity-50 flex items-center justify-center space-x-2"
            data-testid="submit-referral-button"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Gift size={20} />
                <span>Send Referral</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

export default ReviewReferralCard;
